import { CLUBS_MAJORITY_THRESHOLD, POINTS, type PasurDealResult } from "@bing/engine-pasur";

/* ------------------------------------------------------------------ *
 * A DEAL'S BREAKDOWN, PER SIDE (P1 Task 7, W15) — what the deal's card lists, PURE. Every number comes
 * from the deal's result (`lastDeal`, public — never `GameResult`, which carries no `detail`), and the
 * values are the ENGINE's (`POINTS`, `CLUBS_MAJORITY_THRESHOLD`) — the six lines' sum restates
 * `scoreDeal`'s, and `pasur-breakdown.test.ts` pins it against the engine's own on seeded deals, so the
 * card and the engine cannot disagree about what a line is worth.
 * ------------------------------------------------------------------ */

/** The clubs in a deck — the «/۱۳» the clubs line counts toward (13 ranks a suit). */
export const CLUBS_IN_A_DECK = 13;

/** One side's lines for a finished deal — each with the points it scored. */
export interface SideBreakdown {
  /** How many clubs the side took, and the clubs points (7 at ≥ 7, else 0). */
  readonly clubs: { readonly count: number; readonly points: number };
  /** 2♣'s points (2) when the side took it, else 0. */
  readonly twoOfClubs: number;
  /** 10♦'s points (3) when the side took it, else 0. */
  readonly tenOfDiamonds: number;
  /** Aces / Jacks taken, and their points (1 each). */
  readonly aces: { readonly count: number; readonly points: number };
  readonly jacks: { readonly count: number; readonly points: number };
  /** The side's standing سورs after cancellation (D10), and their points (5 each). */
  readonly surs: { readonly count: number; readonly points: number };
  /** The deal's points: the sum of the lines above. */
  readonly points: number;
  /** The side's match total after this deal. */
  readonly total: number;
}

/** Side `side`'s lines in `deal`. */
export function sideBreakdown(deal: PasurDealResult, side: number): SideBreakdown {
  const t = deal.tallies[side];
  const clubs = t?.clubs ?? 0;
  const lines = {
    clubs: { count: clubs, points: clubs >= CLUBS_MAJORITY_THRESHOLD ? POINTS.CLUBS : 0 },
    twoOfClubs: t?.twoOfClubs ? POINTS.TWO_OF_CLUBS : 0,
    tenOfDiamonds: t?.tenOfDiamonds ? POINTS.TEN_OF_DIAMONDS : 0,
    aces: { count: t?.aces ?? 0, points: (t?.aces ?? 0) * POINTS.ACE },
    jacks: { count: t?.jacks ?? 0, points: (t?.jacks ?? 0) * POINTS.JACK },
    surs: { count: t?.surs ?? 0, points: (t?.surs ?? 0) * POINTS.SUR },
  };
  return {
    ...lines,
    points:
      lines.clubs.points +
      lines.twoOfClubs +
      lines.tenOfDiamonds +
      lines.aces.points +
      lines.jacks.points +
      lines.surs.points,
    total: deal.totalsAfter[side] ?? 0,
  };
}

/** The deal a match's END CARD may show: the view's own deal when it was the one that ended (the
 *  engine's terminal keeps the deal's number) — never an older deal behind a match the ROOM ended
 *  mid-deal («opponents-left»): absent beats a misleading line. A frame that carries no deal at all
 *  (none scored yet — or a stand-in frame without the field) shows none. */
export function finalDealOf(view: {
  readonly dealNumber: number;
  readonly lastDeal: PasurDealResult | null;
}): PasurDealResult | null {
  const deal = view.lastDeal;
  return deal && deal.dealNumber === view.dealNumber ? deal : null;
}
